/**
 * Redux slice for user-saved filter sets.
 *
 * Persists named filter presets to Loom User Documents (namespace:
 * "saved-filters"). Each document body is a JSON-serialized
 * `SavedFilterBody`, and the document `title` is the user-facing name.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  createDocument,
  DACW_SERVICE_KEY,
  deleteDocument,
  getDocument,
  getErrorCode,
  isUserDocumentsAvailable,
  listMyDocuments,
  NS_SAVED_FILTERS,
  updateDocument,
  UserDocument,
} from "../app/userDocumentsClient";
import { PropertyFilterGroup } from "../searchSideBar/searchSideBarConnector";

// ---- Types ----

export interface SavedFilter {
  docId: string;
  name: string;
  /** The serialized PropertyFilterGroup that produces the query. */
  propertyFilters: PropertyFilterGroup;
  /** The Lisp DSL query string (for display / quick apply). */
  queryString: string;
  bodyVersion: number;
  updatedAt: string;
}

/** Body stored in the Loom Document. */
interface SavedFilterBody {
  version: number;
  propertyFilters: PropertyFilterGroup;
  queryString: string;
}

const SCHEMA_VERSION = 1;

interface SavedFiltersState {
  filters: SavedFilter[];
  /** Currently active saved filter doc ID (for update/delete targeting). */
  activeDocId: string | null;
  /**
   * Full selection identifier shown in the Select dropdown.
   * Values: "predefined:<id>" | "saved:<docId>" | null (placeholder).
   * Drives the Select's selectedOption — single source of truth.
   */
  activeSelectionId: string | null;
  status: "idle" | "loading" | "saving" | "error";
  error?: string;
}

const initialState: SavedFiltersState = {
  filters: [],
  activeDocId: null,
  activeSelectionId: null,
  status: "idle",
};

// ---- Helpers ----

function parseFilterDoc(doc: UserDocument): SavedFilter | null {
  if (!doc.body) return null;
  try {
    // The Documents API returns body double-stringified.
    let parsed: unknown = JSON.parse(doc.body);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    const body = parsed as SavedFilterBody;
    if (body.version !== SCHEMA_VERSION) return null;
    if (!body.propertyFilters || !body.queryString) return null;
    return {
      docId: doc.docId,
      name: doc.title ?? "Untitled",
      propertyFilters: body.propertyFilters,
      queryString: body.queryString,
      bodyVersion: doc.bodyVersion,
      updatedAt: doc.updatedAt,
    };
  } catch {
    return null;
  }
}

// ---- Thunks ----

export const fetchSavedFilters = createAsyncThunk(
  "savedFilters/fetch",
  async (): Promise<SavedFilter[]> => {
    if (!isUserDocumentsAvailable()) return [];
    try {
      const docs = await listMyDocuments(NS_SAVED_FILTERS, 50);
      // list() doesn't return body — fetch each doc individually.
      // Acceptable fan-out for a small set (< 50 user-saved filters).
      const results = await Promise.allSettled(
        docs.map((d) => getDocument(d.docId))
      );
      const filters: SavedFilter[] = [];
      for (const result of results) {
        if (result.status !== "fulfilled" || !result.value) continue;
        const parsed = parseFilterDoc(result.value);
        if (parsed) filters.push(parsed);
      }
      return filters.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    } catch (e) {
      const code = getErrorCode(e);
      if (code === "FEATURE_DISABLED") {
        // Prod environment — return empty, do not surface error.
        return [];
      }
      throw e;
    }
  }
);

export const createSavedFilter = createAsyncThunk(
  "savedFilters/create",
  async (args: {
    name: string;
    propertyFilters: PropertyFilterGroup;
    queryString: string;
  }): Promise<SavedFilter> => {
    const body: SavedFilterBody = {
      version: SCHEMA_VERSION,
      propertyFilters: args.propertyFilters,
      queryString: args.queryString,
    };
    const doc = await createDocument({
      serviceKey: DACW_SERVICE_KEY,
      namespace: NS_SAVED_FILTERS,
      body: JSON.stringify(body),
      title: args.name,
      visibility: "PRIVATE",
      schemaVersion: SCHEMA_VERSION,
    });
    return {
      docId: doc.docId,
      name: doc.title ?? args.name,
      propertyFilters: args.propertyFilters,
      queryString: args.queryString,
      bodyVersion: doc.bodyVersion,
      updatedAt: doc.updatedAt,
    };
  }
);

export const renameSavedFilter = createAsyncThunk(
  "savedFilters/rename",
  async (args: {
    docId: string;
    newName: string;
    bodyVersion: number;
  }): Promise<{
    docId: string;
    newName: string;
    bodyVersion: number;
  }> => {
    const updated = await updateDocument({
      docId: args.docId,
      title: args.newName,
      expectedVersion: args.bodyVersion,
    });
    return {
      docId: args.docId,
      newName: updated.title ?? args.newName,
      bodyVersion: updated.bodyVersion,
    };
  }
);

export const updateSavedFilter = createAsyncThunk(
  "savedFilters/update",
  async (args: {
    docId: string;
    name: string;
    propertyFilters: PropertyFilterGroup;
    queryString: string;
    bodyVersion: number;
  }): Promise<SavedFilter> => {
    const body: SavedFilterBody = {
      version: SCHEMA_VERSION,
      propertyFilters: args.propertyFilters,
      queryString: args.queryString,
    };
    const updated = await updateDocument({
      docId: args.docId,
      body: JSON.stringify(body),
      expectedVersion: args.bodyVersion,
    });
    return {
      docId: args.docId,
      name: args.name,
      propertyFilters: args.propertyFilters,
      queryString: args.queryString,
      bodyVersion: updated.bodyVersion,
      updatedAt: new Date().toISOString(),
    };
  }
);

export const deleteSavedFilter = createAsyncThunk(
  "savedFilters/delete",
  async (docId: string): Promise<string> => {
    await deleteDocument(docId);
    return docId;
  }
);

// ---- Slice ----

const savedFiltersSlice = createSlice({
  name: "savedFilters",
  initialState,
  reducers: {
    clearSavedFiltersError(state) {
      state.error = undefined;
      state.status = "idle";
    },
    setActiveFilter(state, action: PayloadAction<string>) {
      state.activeDocId = action.payload;
      state.activeSelectionId = `saved:${action.payload}`;
    },
    setActivePredefined(state, action: PayloadAction<string>) {
      state.activeDocId = null;
      state.activeSelectionId = `predefined:${action.payload}`;
    },
    clearActiveFilter(state) {
      state.activeDocId = null;
      state.activeSelectionId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchSavedFilters.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSavedFilters.fulfilled, (state, action) => {
        state.status = "idle";
        state.filters = action.payload;
      })
      .addCase(fetchSavedFilters.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message ?? "Failed to load saved filters";
      })
      // create
      .addCase(createSavedFilter.pending, (state) => {
        state.status = "saving";
      })
      .addCase(createSavedFilter.fulfilled, (state, action) => {
        state.status = "idle";
        state.filters.unshift(action.payload);
      })
      .addCase(createSavedFilter.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message ?? "Failed to save filter";
      })
      // rename
      .addCase(renameSavedFilter.fulfilled, (state, action) => {
        state.status = "idle";
        const { docId, newName, bodyVersion } = action.payload;
        const filter = state.filters.find((f) => f.docId === docId);
        if (filter) {
          filter.name = newName;
          filter.bodyVersion = bodyVersion;
        }
      })
      .addCase(renameSavedFilter.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message ?? "Failed to rename filter";
      })
      // update
      .addCase(updateSavedFilter.pending, (state) => {
        state.status = "saving";
      })
      .addCase(updateSavedFilter.fulfilled, (state, action) => {
        state.status = "idle";
        const idx = state.filters.findIndex(
          (f) => f.docId === action.payload.docId
        );
        if (idx >= 0) {
          state.filters[idx] = action.payload;
        }
      })
      .addCase(updateSavedFilter.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message ?? "Failed to update filter";
      })
      // delete
      .addCase(deleteSavedFilter.fulfilled, (state, action) => {
        state.status = "idle";
        state.filters = state.filters.filter((f) => f.docId !== action.payload);
        if (state.activeDocId === action.payload) {
          state.activeDocId = null;
          state.activeSelectionId = null;
        }
      })
      .addCase(deleteSavedFilter.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message ?? "Failed to delete filter";
      });
  },
});

export const {
  clearSavedFiltersError,
  setActiveFilter,
  setActivePredefined,
  clearActiveFilter,
} = savedFiltersSlice.actions;
export default savedFiltersSlice.reducer;
